import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  ILoggerCallback,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
// Other
import { EnsureModuleLoadedOnceGuard } from '@core/ensure-module-loaded-once-guard';
// Guards
import { PortalAdminGuard, FeatureToggleGuard, CreateAuthPacGuard } from '@core/guards';
// Interceptors/Http
import { HttpErrorHandler, PacimHttpErrorHandlerService, HttpUtility } from '@core/http';
// Services
import {
  MonitoringService,
  AppealsAndGrievancesService,
  AuthDetailsUtilityPacimService,
  AuthenticationService,
  AuthorizationPacimService,
  AuthorizationDetailClinicalRationaleService,
  AuthorizationHistoryService,
  AuthorizationRequestService,
  AuthorizationRequestPacimService,
  AuthorizationService,
  BroadcastAppService,
  BroadcastAuthorizationService,
  CareCoordinationService,
  ClaimService,
  ClaimSearchDetailsService,
  DocumentService,
  DocumentActivityService,
  HomePageService,
  IdleService,
  LetterLogService,
  LookupService,
  LookupPacImService,
  MemberService,
  ModalDialogService,
  OasisService,
  OrderingProviderPacimService,
  PhysicianService,
  ProviderManagementService,
  ProviderService,
  ServiceLinesService,
  TenantService,
  ConfigService,
  CONFIG_ENV,
  AUTH_CONFIG,
  B2C_POLICIES,
  CONFIG_FILE,
  API_BASE_URL,
  API_BASE_PLATFORM_URL,
  API_BASE_PACIM_URL,
  API_BASE_DMEPOS_URL,
  API_BASE_SDOH_URL,
  ConfigB2CPoliciesFactory,
  ConfigFactory,
  ConfigFactoryApiUrl,
  ConfigFactoryApiUrlPlatform,
  ConfigFactoryApiUrlPacIM,
  ConfigFactoryApiDMEPOSUrl,
  ServicingFacilitiesPacimService,
  AuthorizationDetailsPacimService,
  DiagnosisPacimService,
  ServiceLineUtilityService,
  ServiceLinesPacimService,
  ClinicalInformationPacimService,
  NotesService,
  BarriersToDischargeUtilityPacimService,
  MemberPacService,
  PostLoginRedirectService,
  ConfigFactoryApiSDoHUrl,
  UiBlockService,
  HHRoutingService,
  API_BASE_SPECIALTY_PROGRAMS_URL,
  ConfigFactoryApiSpecialtyProgramsUrl,
  InboundFaxService,
  ClinicalResponsesService,
  API_BASE_DOCUMENT_URL,
  ConfigFactoryApiUrlDocument,
  ConfigFactoryApiNotificationsUrl,
  API_BASE_NOTIFICATIONS_URL,
} from '@core/service';
import { FeatureFlags } from '@core/enumeration'
import { IEnvironmentVariables } from 'src/environments/IEnvironment';
import {
  ODataService,
  SecurityModule,
  SessionStorageService,
  FeatureToggleModule,
  LocalStorageService
} from '@mynexus/mynexus-ui-lib';
import { ServiceLinePacimPdsUtilityService } from './service/service-line-pacim-pds-utility.service';
import { BarrierReviewUtilityPacimService } from '@core/service/barrier-review-utility-pacim.service';
import { generateHashKey } from '@shared/utility';
import { UserConfigService } from './service/user-config.service';
import { ReferenceByEntityService } from './service/reference-by-entity.service';
import { SpecialInstructionsService } from './service/special-instructions.service';
import { DomDmeposAuthorizationServiceFactory, DomNotesBroadcastServiceFactory } from './web-components';
import { AdditionalFacilityUtilityService } from './service/additional-facility-utility.service';
import { FormGeneratorService } from '@shared/component/form-generator/form-generator.service';
import { CareCoordinationExactPageService } from './service/care-coordination-exact-page.service';
import { IcaService } from './service/ica.service';
import { DatePipe } from '@angular/common';
import { AuthorizationDecisionService } from './service/authorization-decision.service';
import { FaxService } from './service/fax.service';

export function MSALInstanceFactory(appInsights: MonitoringService, config: IEnvironmentVariables): IPublicClientApplication {
  const loggerCallbackFn: ILoggerCallback = (_ , message) => {
    console.info('[MSAL]', message);
  };

  const queryParams = new URLSearchParams(window.location.search);
  const { names: userFlows } = config.b2cPolicies;
  let redirectUri = config.authConfig.config.auth.redirectUri;
  let authorityUri = `${config.b2cPolicies.authorityBaseUrl}/`;

  // Need to determine which tenant user is logging in from
  if (window.location.host.includes('regence') || queryParams.get('login') === 'regence') {
    redirectUri = redirectUri.replace('{client}', 'regence.');
    authorityUri += userFlows.signUpSignInRegence;
  } else if (window.location.host.includes('extsso') || queryParams.get('login') === 'extsso') {
    redirectUri = config.extssoBaseUrl;
    authorityUri += userFlows.signUpSignInportalextsso;
  } else {
    redirectUri = redirectUri.replace('{client}', '');
    authorityUri += userFlows.signUpSignIn;
  }

  const clientApp = new PublicClientApplication({
    ...config.authConfig.config,
    auth: {
      ...config.authConfig.config.auth,
      redirectUri,
      authority: authorityUri,
      postLogoutRedirectUri: redirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage
    },
    system: {
      loggerOptions: {
        loggerCallback: loggerCallbackFn,
        logLevel: LogLevel.Error,
      }
    }
  });

  // Note: Do NOT call clientApp.initialize() here.
  // MSAL Angular v5 handles initialization internally via the MSAL_INSTANCE provider.

  return clientApp;
}

export function MSALInterceptorConfigFactory(config: IEnvironmentVariables): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  config.authConfig.angularConfig.protectedResourceMap.forEach(([uri, scopes]) => {
    protectedResourceMap.set(uri, scopes);
  });

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(config: IEnvironmentVariables): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...config.authConfig.angularConfig.consentScopes],
      ...config.authConfig.angularConfig.extraQueryParameters
    }
  };
}

@NgModule({ imports: [MsalModule,
        // todo: provide proper typing
        FeatureToggleModule.forRoot(API_BASE_URL),
        SecurityModule.forRoot([
            {
                url: API_BASE_URL,
            },
            {
                url: API_BASE_PACIM_URL,
                feature: FeatureFlags.PACIM,
            },
            {
                url: API_BASE_DMEPOS_URL
            },
        ], 'home')], providers: [
        ConfigService,
        { provide: CONFIG_FILE, useValue: `../config.json?v=${generateHashKey()}` },
        { provide: 'AUTH_CONFIG_PROP', useValue: 'authConfig' },
        {
            provide: API_BASE_URL,
            useFactory: ConfigFactoryApiUrl,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_PLATFORM_URL,
            useFactory: ConfigFactoryApiUrlPlatform,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_DOCUMENT_URL,
            useFactory: ConfigFactoryApiUrlDocument,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_PACIM_URL,
            useFactory: ConfigFactoryApiUrlPacIM,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_DMEPOS_URL,
            useFactory: ConfigFactoryApiDMEPOSUrl,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_SDOH_URL,
            useFactory: ConfigFactoryApiSDoHUrl,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_SPECIALTY_PROGRAMS_URL,
            useFactory: ConfigFactoryApiSpecialtyProgramsUrl,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: API_BASE_NOTIFICATIONS_URL,
            useFactory: ConfigFactoryApiNotificationsUrl,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: AUTH_CONFIG,
            useFactory: ConfigFactory,
            deps: [ConfigService, CONFIG_FILE, 'AUTH_CONFIG_PROP'],
        },
        {
            provide: CONFIG_ENV,
            useFactory: ConfigFactory,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: B2C_POLICIES,
            useFactory: ConfigB2CPoliciesFactory,
            deps: [ConfigService, CONFIG_FILE],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
            deps: [MonitoringService, CONFIG_ENV],
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
            deps: [CONFIG_ENV],
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
            deps: [CONFIG_ENV],
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        PortalAdminGuard,
        FeatureToggleGuard,
        CreateAuthPacGuard,
        AuthenticationService,
        AuthorizationDetailClinicalRationaleService,
        AuthorizationHistoryService,
        AuthorizationRequestService,
        AuthorizationDecisionService,
        AuthorizationRequestPacimService,
        AuthorizationService,
        MonitoringService,
        AppealsAndGrievancesService,
        BroadcastAppService,
        BroadcastAuthorizationService,
        CareCoordinationService,
        CareCoordinationExactPageService,
        ClaimService,
        ClaimSearchDetailsService,
        DocumentService,
        DocumentActivityService,
        HomePageService,
        HttpErrorHandler,
        PacimHttpErrorHandlerService,
        HttpUtility,
        IdleService,
        LetterLogService,
        LookupService,
        LookupPacImService,
        MemberService,
        ClinicalResponsesService,
        ReferenceByEntityService,
        SpecialInstructionsService,
        MemberPacService,
        ModalDialogService,
        OasisService,
        IcaService,
        InboundFaxService,
        ODataService,
        PhysicianService,
        ProviderService,
        ProviderManagementService,
        ServiceLinesService,
        SessionStorageService,
        LocalStorageService,
        TenantService,
        { provide: 'windowRef', useValue: window },
        { provide: 'documentRef', useValue: document },
        OrderingProviderPacimService,
        AuthDetailsUtilityPacimService,
        AuthorizationPacimService,
        ServicingFacilitiesPacimService,
        AuthorizationDetailsPacimService,
        DiagnosisPacimService,
        ServiceLineUtilityService,
        ServiceLinesPacimService,
        UserConfigService,
        ClinicalInformationPacimService,
        ServiceLinePacimPdsUtilityService,
        BarriersToDischargeUtilityPacimService,
        BarrierReviewUtilityPacimService,
        NotesService,
        FaxService,
        DomNotesBroadcastServiceFactory,
        DomDmeposAuthorizationServiceFactory,
        PostLoginRedirectService,
        UiBlockService,
        FormGeneratorService,
        HHRoutingService,
        AdditionalFacilityUtilityService,
        DatePipe,
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class CoreModule extends EnsureModuleLoadedOnceGuard {
  // Ensure that CoreModule is only loaded into AppModule
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    super(parentModule);
  }
}
